import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { TextField, View } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Box, Card } from "theme-ui";
import {
  Heading,
  Button as LumosButton,
  useNotify,
} from "@loanpal/lumos__core";
import { mintPaymentLink } from "../../utils/api";
import logo from "../../images/goodleap-white-orange.svg";

/** Same Passion / Grape tokens as the pay flow. */
const PAY = {
  radiusSm: "10px",
  accent: "#472BA4",
  accentHover: "#200F51",
  accentSoft: "rgba(71, 43, 164, 0.1)",
  pageBg: "#F6F5F4",
};

const primaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  bg: PAY.accent,
  color: "white",
  border: "1px solid",
  borderColor: PAY.accent,
  "&:hover": {
    bg: PAY.accentHover,
    borderColor: PAY.accentHover,
    color: "white",
  },
  "&:disabled": {
    opacity: 0.5,
    cursor: "default",
  },
};

const secondaryButtonSx = {
  borderRadius: PAY.radiusSm,
  height: 40,
  px: 3,
  fontWeight: 600,
  color: PAY.accent,
  borderColor: PAY.accent,
  bg: "transparent",
  "&:hover": {
    bg: PAY.accentSoft,
    color: PAY.accentHover,
    borderColor: PAY.accentHover,
  },
};

/**
 * Public helper page to generate a signed Pay Link (SharedApp id + Reference ID).
 * Same as the public POST /payment-link API — no partner API keys or Cognito.
 *
 * /pay/generate/:id pre-fills (and locks) the pay link id.
 */
const GeneratePaymentLink = () => {
  const { id: idFromPath } = useParams();
  const lockedPayId = String(idFromPath || "").trim();
  const { addSnack, addToast } = useNotify();
  const [payLinkId, setPayLinkId] = useState(lockedPayId);
  const [ref, setRef] = useState("");
  const [ttlDays, setTtlDays] = useState("90");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (lockedPayId) setPayLinkId(lockedPayId);
  }, [lockedPayId]);

  const handleGenerate = async () => {
    const sid = (lockedPayId || payLinkId).trim();
    const reference = ref.trim();
    if (!sid || !reference) {
      addSnack({
        variant: "warning",
        message: "PayLink ID and Reference ID are required.",
      });
      return;
    }
    const days = Number(ttlDays);
    const ttlSeconds =
      Number.isFinite(days) && days > 0
        ? Math.floor(days * 24 * 60 * 60)
        : undefined;

    setBusy(true);
    setResult(null);
    try {
      const data = await mintPaymentLink({
        payLinkId: sid,
        ref: reference,
        ttlSeconds,
      });
      setResult(data);
      addToast({ variant: "success", message: "Payment link created." });
    } catch (err) {
      console.log("mint payment link failed", err);
      addSnack({
        variant: "danger",
        message: err?.message || "Failed to create payment link",
      });
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    const url = result?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      addToast({ variant: "success", message: "Copied to clipboard." });
    } catch {
      addSnack({ variant: "danger", message: "Could not copy link." });
    }
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", bg: PAY.pageBg, minHeight: "100vh" }}>
      <Box>
        <Heading
          className="heading-box"
          sx={{
            width: 960,
            maxWidth: "100%",
            margin: "0 auto",
            background:
              "linear-gradient(93.207deg, #200F51 0%, #200F51 40%, #472BA4 160%)",
            borderBottom: "4px solid #FF8300",
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            overflow: "hidden",
          }}
        >
          <img
            src={logo}
            alt="GoodLeap"
            style={{
              width: 150,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: 4,
            }}
            className="logo"
          />
        </Heading>
      </Box>

      <Box sx={{ maxWidth: 720, mx: "auto", px: 3, py: 4 }}>
        <Heading variant="h4" sx={{ mb: 2, color: PAY.accentHover }}>
          Generate Payment Link
        </Heading>
        <Box sx={{ mb: 3, color: "neutral.d2", fontSize: 2 }}>
          Pass your PayLink ID and reference ID.
        </Box>

        <Card variant="float" sx={{ p: 4 }}>
          <View>
            <TextField
              label="PayLink ID"
              descriptiveText="Get it from http://pay.goodleap.link/{payLinkId}"
              value={lockedPayId || payLinkId}
              onChange={(e) => {
                if (lockedPayId) return;
                setPayLinkId(e.target.value);
              }}
              isReadOnly={Boolean(lockedPayId)}
              isDisabled={Boolean(lockedPayId)}
              marginBottom="1rem"
            />
            <TextField
              label="Reference ID"
              descriptiveText="Order / invoice reference locked on the pay form"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              marginBottom="1rem"
            />
            <TextField
              label="Link TTL (days)"
              type="number"
              value={ttlDays}
              onChange={(e) => setTtlDays(e.target.value)}
              marginBottom="1.5rem"
            />
            <LumosButton
              type="button"
              onClick={handleGenerate}
              disabled={busy}
              sx={primaryButtonSx}
            >
              {busy ? "Generating…" : "Generate signed link"}
            </LumosButton>
          </View>

          {result?.url ? (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ fontWeight: 600, mb: 2 }}>Signed URL</Box>
              <Box
                as="input"
                readOnly
                value={result.url}
                onClick={(e) => e.target.select?.()}
                sx={{
                  width: "100%",
                  fontSize: 1,
                  p: 2,
                  mb: 2,
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "neutral.l3",
                  bg: "neutral.l4",
                }}
              />
              <LumosButton
                type="button"
                variant="secondary"
                onClick={copyUrl}
                sx={secondaryButtonSx}
              >
                Copy link
              </LumosButton>
              <Box sx={{ mt: 3, fontSize: 1, color: "neutral.d2" }}>
                Expires: {result.expiresAt || "—"}
                <br />
                Ref: {result.ref}
              </Box>
            </Box>
          ) : null}
        </Card>
      </Box>
    </Box>
  );
};

export default GeneratePaymentLink;
